import { useState, useEffect, useCallback, useRef } from "react";
import { App } from "@exiftrator/shared";
import type { LicenseAdapter } from "@exiftrator/shared/types";
import { DemoProvider } from "@exiftrator/shared/contexts";
import { loadDemoFiles } from "@exiftrator/shared/utils/loadDemoFiles";
import { Loader } from "lucide-react";
import { usePageTitle } from "@exiftrator/shared/hooks";
import { formatPageTitle } from "@exiftrator/shared/utils";

// Passthrough router — since BrowserRouter is already at the top level,
// App doesn't need its own router. This satisfies the router prop type.
function PassthroughRouter({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function DemoPageContent({ licenseAdapter }: { licenseAdapter: LicenseAdapter }) {
  usePageTitle(formatPageTitle("Demo"));
  const [demoFiles, setDemoFiles] = useState<File[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const isLoadingRef = useRef(true);

  // Load demo files on mount
  useEffect(() => {
    const loadFiles = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const files = await loadDemoFiles();
        setDemoFiles(files);
      } catch (err) {
        console.error("Failed to load demo files:", err);
        setError("Failed to load demo images. Please try again.");
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadFiles();
  }, [resetKey]);

  const resetDemo = useCallback(() => {
    setResetKey((prev) => prev + 1);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-bg-deep text-text-primary">
        <Loader className="w-12 h-12 animate-spin text-accent-primary mb-4" />
        <p className="text-lg">Loading demo images...</p>
      </div>
    );
  }

  if (error || !demoFiles) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-bg-deep text-text-primary">
        <div className="text-center max-w-md">
          <p className="text-lg text-destructive mb-4">{error || "Failed to load demo images"}</p>
          <button
            onClick={() => setResetKey((prev) => prev + 1)}
            className="px-4 py-2 bg-accent-primary text-white rounded-lg hover:opacity-90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DemoProvider isDemo={true} resetDemo={resetDemo}>
      <App
        key={resetKey}
        router={PassthroughRouter}
        basePath="/demo"
        initialFiles={demoFiles}
        licenseAdapter={licenseAdapter}
      />
    </DemoProvider>
  );
}

export default function DemoPage({ licenseAdapter }: { licenseAdapter: LicenseAdapter }) {
  return <DemoPageContent licenseAdapter={licenseAdapter} />;
}
